import { WelockLogo } from "@/components/WelockMark";
import { RobinhoodMark } from "@/components/RobinhoodMark";
import { LayoutGrid, Lock, Layers, Clock, Send, Wallet, Cpu } from "lucide-react";

const NAV = [
  { icon: LayoutGrid, label: "Overview", active: true },
  { icon: Lock, label: "Token Locker" },
  { icon: Layers, label: "LP Locker" },
  { icon: Clock, label: "Vesting" },
  { icon: Send, label: "Multisender" },
  { icon: Wallet, label: "Portfolio" },
  { icon: Cpu, label: "Agents API" },
];

const STATS = [
  { label: "Protocol TVL", value: "$41.5M", accent: true },
  { label: "Active Locks", value: "1,428" },
  { label: "Your Value", value: "$3.20M" },
  { label: "Next Unlock", value: "12d 04h" },
];

const ROWS = [
  { s: "WLK", a: "2,400,000", v: "$2.01M", st: "Active" },
  { s: "RBH", a: "540,000", v: "$1.24M", st: "Active" },
  { s: "USDC", a: "180,000", v: "$180K", st: "Claimable" },
  { s: "ETH", a: "42.5", v: "$132K", st: "Active" },
];

// Static area-chart path
const AREA = "M0,70 L40,58 L80,62 L120,40 L160,46 L200,26 L240,34 L280,18 L320,24 L360,10 L400,16";

export function DashboardPreview() {
  return (
    <div className="w-full overflow-hidden border border-welock-border bg-welock-surface shadow-[0_-20px_80px_-20px_rgba(0,0,0,0.8)]">
      {/* window chrome */}
      <div className="flex items-center justify-between border-b border-welock-border bg-welock-bg/60 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-welock-border" />
            <span className="h-2.5 w-2.5 rounded-full bg-welock-border" />
            <span className="h-2.5 w-2.5 rounded-full bg-welock-accent/60" />
          </div>
          <span className="hidden font-mono text-[10px] text-welock-textMuted sm:block">
            app.welock.xyz/overview
          </span>
        </div>
        <RobinhoodMark size={14} withLabel />
      </div>

      <div className="flex">
        {/* sidebar */}
        <aside className="hidden w-48 shrink-0 flex-col border-r border-welock-border p-3 sm:flex">
          <div className="mb-4 px-2">
            <WelockLogo />
          </div>
          {NAV.map((n) => (
            <div
              key={n.label}
              className={`mb-0.5 flex items-center gap-2.5 border-l-2 px-3 py-2 font-mono text-[11px] ${
                n.active
                  ? "border-welock-accent bg-welock-bg text-welock-textPrimary"
                  : "border-transparent text-welock-textSecondary"
              }`}
            >
              <n.icon size={13} className={n.active ? "text-welock-accent" : ""} />
              {n.label}
            </div>
          ))}
        </aside>

        {/* content */}
        <div className="flex-1 p-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="border border-welock-border bg-welock-bg p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-welock-textMuted">
                  {s.label}
                </div>
                <div
                  className={`mt-2 font-heading text-lg font-medium ${
                    s.accent ? "text-welock-accent" : "text-welock-textPrimary"
                  }`}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>

          {/* chart */}
          <div className="mt-3 border border-welock-border bg-welock-bg p-4">
            <div className="mb-3 font-mono text-[9px] uppercase tracking-widest text-welock-textMuted">
              Unlock schedule
            </div>
            <svg viewBox="0 0 400 80" className="h-24 w-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="pv-g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00DC82" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#00DC82" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[20, 40, 60].map((y) => (
                <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#1E222D" strokeWidth="1" />
              ))}
              <path d={`${AREA} L400,80 L0,80 Z`} fill="url(#pv-g)" />
              <path d={AREA} fill="none" stroke="#00DC82" strokeWidth="1.5" />
            </svg>
          </div>

          {/* table */}
          <div className="mt-3 border border-welock-border bg-welock-bg">
            <div className="grid grid-cols-4 gap-2 border-b border-welock-border px-4 py-2 font-mono text-[9px] uppercase tracking-widest text-welock-textMuted">
              <span>Asset</span><span>Amount</span><span>Value</span><span className="text-right">Status</span>
            </div>
            {ROWS.map((r) => (
              <div key={r.s} className="grid grid-cols-4 items-center gap-2 border-b border-welock-border/50 px-4 py-2.5 font-mono text-[11px] last:border-0">
                <span className="text-welock-textPrimary">{r.s}</span>
                <span className="text-welock-textSecondary">{r.a}</span>
                <span className="text-welock-textPrimary">{r.v}</span>
                <span className="flex justify-end">
                  <span className={`border px-2 py-0.5 text-[9px] uppercase tracking-wider ${
                    r.st === "Claimable"
                      ? "border-welock-warning/30 bg-welock-warning/5 text-welock-warning"
                      : "border-welock-accent/30 bg-welock-accent/5 text-welock-accent"
                  }`}>
                    {r.st}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
